import { useEffect, useState, useContext, useRef } from "react";

import useAxios from "../../utils/useAxios";
import { resObj, paginationVal } from "../../utils/constants";
import PermissionContext from "../../context/PermissionContext";
import DrawerContext from "../../context/DrawerContext";
import AutoLogout from "../../utils/useAutoLogout";

import Pagination from '@mui/material/Pagination';

import SideDrawer from "../../components/layout/SideDrawer";
import Loader from "../../components/layout/Loader";
import ListHeader from "../../components/layout/ListHeader";
import CustomTable from "../../components/layout/CustomTable";
import SuccessAlert from "../../components/layout/SuccessAlert";
import NoData from "../../components/layout/NoData";
import ErrorModal from "../../components/layout/ErrorModal";
import InputModalForm from "../../components/layout/InputModalForm";
import DeleteModal from "../../components/layout/DeleteModal";

const ProjectListPage = () => {
  const api = useAxios();

  const { permisionsList, checkUserPermission } = useContext(PermissionContext);
  const { drawerValue, setDrawerValue } = useContext(DrawerContext);

  useEffect(() => {
    if (permisionsList.length > 0) checkUserPermission(['Admin-View Project List']);
  }, [permisionsList]);

  const tableHeading = ['SI No', 'Project Name'];
  const deleteModalTitle = 'Project Delete';
  const deleteModalMsg = 'Are you sure you want to delete (Project will be lost)?';

  const pageData = useRef({ ...paginationVal });
  const [apiRes, setApiRes] = useState(resObj);
  const [projectList, setOProjectList] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    getProjects();
  }, []);

  const getProjects = async () => {
    try {
      setApiRes({ ...apiRes, loading: true });
      const response = await api.get('/api/user/project/list/get', {
        params: {
          search_term: pageData.current.searchTerm,
          page_number: +pageData.current.pageNumber,
        }
      });

      if (response.status === 200) {
        setApiRes({ ...apiRes, loading: false });
        setOProjectList(response.data.data);
        pageData.current.pageCount = response.data.page_count;
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Projects Get',
        loading: false,
      });
    }
  };

  const navigateToEdit = oid => {
    return;
  };

  const projectDelete = async () => {
    try {
      setApiRes({ ...apiRes, loading: true });
      const response = await api.delete(`/api/user/project/delete/${projectId}`);

      if (response.status === 200) {
        setDeleteModalOpen(false);
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: 'Project deleted successfully',
          loading: false,
        });
      }
      getProjects();
    } catch (err) {
      setDeleteModalOpen(false);
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Project Delete',
        loading: false,
      });
    }
  };

  const handlePaginationClick = (e, pageNumber) => {
    pageData.current.pageNumber = pageNumber;
    getProjects();
  };

  const projectCreate = async () => {
    try {
      setApiRes({ ...apiRes, loading: true });
      const response = await api.post('/api/user/project/create', {
        project_name: projectName
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
        setShowProjectModal(false);
        setProjectName('');
        getProjects();
      }
    } catch (err) {
      const errMsg = JSON.stringify(err.response.data).replace(/[{}[\]"']/g, '');
      if (errMsg === 'Project already exists.') {
        setFormErrors({
          projectName: 'Project already exists.'
        });
        setApiRes({ ...apiRes, loading: false });
      }
      else {
        setShowProjectModal(false);
        setProjectName('');
        setApiRes({
          ...apiRes,
          axiosError: true,
          errMsg: errMsg,
          errHeading: 'Project Create',
          loading: false,
        });
      }
    }
  };

  const validate = () => {
    const errors = {};
    if (!projectName) {
      errors.projectName = 'Project name is required!';
    }
    return errors;
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      projectCreate();
    }
  }, [formErrors]);

  const handleSubmit = e => {
    e.preventDefault();
    setFormErrors(validate());
    setIsSubmit(true);
  };

  return (
    <AutoLogout>
      <SideDrawer drawerValue={drawerValue} setDrawerValue={setDrawerValue} />
      {apiRes.loading && <Loader />}
      <div className="mainContainer" style={{ marginLeft: drawerValue.drawerWidth }}>
        {apiRes.showAlert && <SuccessAlert apiRes={apiRes} setApiRes={setApiRes} />}
        <ListHeader
          title="Projects"
          handleClick={() => setShowProjectModal(true)}
          search={getProjects}
          pageData={pageData}
          hasCreatePermission={permisionsList.includes('Admin-Project Create')}
        />

        <CustomTable
          heading={tableHeading}
          rows={projectList}
          navigateToEdit={navigateToEdit}
          setDeleteModalOpen={setDeleteModalOpen}
          setId={setProjectId}
          pageData={pageData}
          idKey={'id'}
          isDeleteBtnVisible={permisionsList.includes('Admin-Manage Project')}
          showToolTip={false}
        />

        {projectList.length === 0 && <NoData />}

        {
          (projectList.length !== 0 && pageData.current.pageCount !== 1)
          &&
          <Pagination
            style={{ marginTop: '10px', float: 'right' }}
            count={pageData.current.pageCount}
            onChange={handlePaginationClick}
          />
        }

        <InputModalForm
          title={'Project Create'}
          label={'Project Name'}
          show={showProjectModal}
          setShow={setShowProjectModal}
          value={projectName}
          setValue={setProjectName}
          formErrors={formErrors}
          errorKey={'projectName'}
          handleSubmit={handleSubmit}
        />

        <DeleteModal
          title={deleteModalTitle}
          message={deleteModalMsg}
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          onDelete={projectDelete}
        />

        <ErrorModal apiRes={apiRes} setApiRes={setApiRes} />

      </div>
    </AutoLogout>
  );
};

export default ProjectListPage;